import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Copy, ExternalLink, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "../../lib/api";
import { VisitBadge } from "../../components/admin/VisitBadge";

const shareUrl = (slug) => `${window.location.origin}/api/share/${slug}`;

const Row = ({ p, onDelete }) => {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl(p.slug));
      toast.success("Share link copied. It shows the photo and headline in WhatsApp and texts.");
    } catch {
      toast.error("Couldn't copy. Select the link and copy it manually.");
    }
  };
  return (
    <li data-testid={`pitch-row-${p.slug}`} className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 md:flex-row md:items-center md:justify-between">
      <div className="min-w-0">
        <p className="font-display text-xl">{p.customerName}</p>
        <p className="text-sm text-slate-600">{p.vesselName}</p>
        <p data-testid={`share-url-${p.slug}`} className="mt-1 break-all font-mono text-xs text-[#9a5a35]">/api/share/{p.slug}</p>
        <VisitBadge p={p} />
      </div>
      <div className="flex flex-wrap gap-2">
        <button data-testid={`copy-link-${p.slug}`} onClick={copy} className="flex items-center gap-1.5 rounded-full border border-slate-300 px-3.5 py-2 text-sm hover:bg-slate-100"><Copy className="h-4 w-4" />Copy share link</button>
        <a data-testid={`open-${p.slug}`} href={`/p/${p.slug}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 rounded-full border border-slate-300 px-3.5 py-2 text-sm hover:bg-slate-100"><ExternalLink className="h-4 w-4" />View</a>
        <Link data-testid={`edit-${p.slug}`} to={`/admin/edit/${p.slug}`} className="flex items-center gap-1.5 rounded-full bg-[#0B192C] px-3.5 py-2 text-sm text-white hover:bg-[#1E3E62]"><Pencil className="h-4 w-4" />Edit</Link>
        <button data-testid={`delete-${p.slug}`} onClick={() => onDelete(p)} className="flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm text-red-700 hover:bg-red-50"><Trash2 className="h-4 w-4" />Delete</button>
      </div>
    </li>
  );
};

export default function AdminDashboard() {
  const [items, setItems] = useState(null);

  const load = () => api.get("/admin/pitches").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch((e) => { toast.error(errorText(e)); setItems([]); });
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const remove = async (p) => {
    if (!window.confirm(`Delete the page for ${p.customerName}? Its link will stop working.`)) return;
    try {
      await api.delete(`/admin/pitches/${p.slug}`);
      toast.success("Page deleted");
      load();
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl">Customer pages</h1>
          <p className="mt-1 text-slate-600">Each customer gets their own link. You'll see when they first open it.</p>
          <p className="mt-1 text-xs text-slate-500">Your own visits while logged in aren't counted.</p>
        </div>
        <Link data-testid="new-pitch-btn" to="/admin/new" className="flex items-center gap-2 rounded-full bg-[#C87D55] px-5 py-3 font-semibold text-[#0B192C] transition-colors duration-200 hover:bg-[#D4A373]">
          <Plus className="h-4 w-4" /> New customer page
        </Link>
      </div>
      {items === null ? (
        <Loader2 className="mt-10 h-6 w-6 animate-spin text-slate-500" />
      ) : items.length === 0 ? (
        <p data-testid="pitch-list-empty" className="mt-10 rounded-2xl border border-dashed border-slate-300 p-8 text-center text-slate-600">No pages yet. Create your first one.</p>
      ) : (
        <ul data-testid="pitch-list" className="mt-8 space-y-3">{items.map((p) => <Row key={p.slug} p={p} onDelete={remove} />)}</ul>
      )}
    </div>
  );
}
